import _slicedToArray from "/home/shipsy/crm-logistics-frontend/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { arc } from 'd3-shape';
import { isPolar, isHelix, isTranspose } from '../../utils/coordinate';
import { select } from '../../utils/selection';
import { sub } from '../../utils/vector';
import { clamp } from '../../utils/number';
import { applyStyle, getArcObject, reorder, toOpacityKey } from '../utils';
// Render rect in different coordinate.
export function rect(document, points, value, coordinate) {
  var style = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  var _style$inset = style.inset,
    inset = _style$inset === void 0 ? 0 : _style$inset,
    _style$radius = style.radius,
    radius = _style$radius === void 0 ? 0 : _style$radius,
    _style$insetLeft = style.insetLeft,
    insetLeft = _style$insetLeft === void 0 ? inset : _style$insetLeft,
    _style$insetTop = style.insetTop,
    insetTop = _style$insetTop === void 0 ? inset : _style$insetTop,
    _style$insetRight = style.insetRight,
    insetRight = _style$insetRight === void 0 ? inset : _style$insetRight,
    _style$insetBottom = style.insetBottom,
    insetBottom = _style$insetBottom === void 0 ? inset : _style$insetBottom,
    _style$radiusBottomLe = style.radiusBottomLeft,
    radiusBottomLeft = _style$radiusBottomLe === void 0 ? radius : _style$radiusBottomLe,
    _style$radiusBottomRi = style.radiusBottomRight,
    radiusBottomRight = _style$radiusBottomRi === void 0 ? radius : _style$radiusBottomRi,
    _style$radiusTopLeft = style.radiusTopLeft,
    radiusTopLeft = _style$radiusTopLeft === void 0 ? radius : _style$radiusTopLeft,
    _style$radiusTopRight = style.radiusTopRight,
    radiusTopRight = _style$radiusTopRight === void 0 ? radius : _style$radiusTopRight,
    _style$minWidth = style.minWidth,
    minWidth = _style$minWidth === void 0 ? -Infinity : _style$minWidth,
    _style$maxWidth = style.maxWidth,
    maxWidth = _style$maxWidth === void 0 ? Infinity : _style$maxWidth,
    rest = __rest(style, ["inset", "radius", "insetLeft", "insetTop", "insetRight", "insetBottom", "radiusBottomLeft", "radiusBottomRight", "radiusTopLeft", "radiusTopRight", "minWidth", "maxWidth"]);
  if (!isPolar(coordinate) && !isHelix(coordinate)) {
    var tpShape = !!isTranspose(coordinate);
    var _ref = tpShape ? reorder(points) : points,
      _ref2 = _slicedToArray(_ref, 3),
      p0 = _ref2[0],
      p2 = _ref2[2];
    var _p = _slicedToArray(p0, 2),
      x = _p[0],
      _y = _p[1];
    var _sub = sub(p2, p0),
      _sub2 = _slicedToArray(_sub, 2),
      width = _sub2[0],
      height = _sub2[1]; // Deal with width or height is negative.
    var absX = width > 0 ? x : x + width;
    var absY = height > 0 ? _y : _y + height;
    var absWidth = Math.abs(width);
    var absHeight = Math.abs(height);
    var finalX = absX + insetLeft;
    var finalY = absY + insetTop;
    var finalWidth = absWidth - (insetLeft + insetRight);
    var finalHeight = absHeight - (insetTop + insetBottom);
    var clampWidth = tpShape ? finalWidth : clamp(finalWidth, minWidth, maxWidth);
    var clampHeight = tpShape ? clamp(finalHeight, minWidth, maxWidth) : finalHeight;
    var clampX = finalX - (clampWidth - finalWidth) / 2;
    var clampY = finalY - (clampHeight - finalHeight) / 2;
    return select(document.createElement('rect', {})).style('x', clampX).style('y', clampY).style('width', clampWidth).style('height', clampHeight).style('radius', [radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft]).call(applyStyle, rest).node();
  }
  // Render path in polar coordinate.
  var y = value.y,
    y1 = value.y1;
  var center = coordinate.getCenter();
  var arcObject = getArcObject(coordinate, points, [y, y1]);
  var path = arc().cornerRadius(radius).padAngle(inset * Math.PI / 180);
  return select(document.createElement('path', {})).style('path', path(arcObject)).style('transform', "translate(".concat(center[0], ", ").concat(center[1], ")")).style('radius', radius).style('inset', inset).call(applyStyle, rest).node();
}
/**
 * Render rect in different coordinate.
 * Calc arc path based on control points directly rather startAngle, endAngle, innerRadius,
 * outerRadius. This is not accurate and will cause bug when the range of y scale is [1, 0]
 * for cell geometry.
 */
export var Color = function Color(options, context) {
  // Render border only when colorAttribute is stroke.
  var colorAttribute = options.colorAttribute,
    _options$opacityAttri = options.opacityAttribute,
    opacityAttribute = _options$opacityAttri === void 0 ? 'fill' : _options$opacityAttri,
    _options$first = options.first,
    first = _options$first === void 0 ? true : _options$first,
    _options$last = options.last,
    last = _options$last === void 0 ? true : _options$last,
    style = __rest(options, ["colorAttribute", "opacityAttribute", "first", "last"]);
  var coordinate = context.coordinate,
    document = context.document;
  return function (points, value, defaults) {
    var defaultColor = defaults.color,
      _defaults$radius = defaults.radius,
      defaultRadius = _defaults$radius === void 0 ? 0 : _defaults$radius,
      restDefaults = __rest(defaults, ["color", "radius"]);
    var defaultLineWidth = restDefaults.lineWidth || 1;
    var stroke = style.stroke,
      _style$radius2 = style.radius,
      radius = _style$radius2 === void 0 ? defaultRadius : _style$radius2,
      _style$radiusTopLeft2 = style.radiusTopLeft,
      radiusTopLeft = _style$radiusTopLeft2 === void 0 ? radius : _style$radiusTopLeft2,
      _style$radiusTopRight2 = style.radiusTopRight,
      radiusTopRight = _style$radiusTopRight2 === void 0 ? radius : _style$radiusTopRight2,
      _style$radiusBottomRi2 = style.radiusBottomRight,
      radiusBottomRight = _style$radiusBottomRi2 === void 0 ? radius : _style$radiusBottomRi2,
      _style$radiusBottomLe2 = style.radiusBottomLeft,
      radiusBottomLeft = _style$radiusBottomLe2 === void 0 ? radius : _style$radiusBottomLe2,
      _style$innerRadius = style.innerRadius,
      innerRadius = _style$innerRadius === void 0 ? 0 : _style$innerRadius,
      _style$innerRadiusTop = style.innerRadiusTopLeft,
      innerRadiusTopLeft = _style$innerRadiusTop === void 0 ? innerRadius : _style$innerRadiusTop,
      _style$innerRadiusTop2 = style.innerRadiusTopRight,
      innerRadiusTopRight = _style$innerRadiusTop2 === void 0 ? innerRadius : _style$innerRadiusTop2,
      _style$innerRadiusBot = style.innerRadiusBottomRight,
      innerRadiusBottomRight = _style$innerRadiusBot === void 0 ? innerRadius : _style$innerRadiusBot,
      _style$innerRadiusBot2 = style.innerRadiusBottomLeft,
      innerRadiusBottomLeft = _style$innerRadiusBot2 === void 0 ? innerRadius : _style$innerRadiusBot2,
      _style$lineWidth = style.lineWidth,
      lineWidth = _style$lineWidth === void 0 ? colorAttribute === 'stroke' || stroke ? defaultLineWidth : 0 : _style$lineWidth,
      _style$inset2 = style.inset,
      inset = _style$inset2 === void 0 ? 0 : _style$inset2,
      _style$insetLeft2 = style.insetLeft,
      insetLeft = _style$insetLeft2 === void 0 ? inset : _style$insetLeft2,
      _style$insetRight2 = style.insetRight,
      insetRight = _style$insetRight2 === void 0 ? inset : _style$insetRight2,
      _style$insetBottom2 = style.insetBottom,
      insetBottom = _style$insetBottom2 === void 0 ? inset : _style$insetBottom2,
      _style$insetTop2 = style.insetTop,
      insetTop = _style$insetTop2 === void 0 ? inset : _style$insetTop2,
      minWidth = style.minWidth,
      maxWidth = style.maxWidth,
      rest = __rest(style, ["stroke", "radius", "radiusTopLeft", "radiusTopRight", "radiusBottomRight", "radiusBottomLeft", "innerRadius", "innerRadiusTopLeft", "innerRadiusTopRight", "innerRadiusBottomRight", "innerRadiusBottomLeft", "lineWidth", "inset", "insetLeft", "insetRight", "insetBottom", "insetTop", "minWidth", "maxWidth"]);
    var _value$color = value.color,
      color = _value$color === void 0 ? defaultColor : _value$color,
      opacity = value.opacity; // Extended style, which is not supported by native g shape,
    // should apply at first.
    var standardDirRadius = [first ? radiusTopLeft : innerRadiusTopLeft, first ? radiusTopRight : innerRadiusTopRight, last ? radiusBottomRight : innerRadiusBottomRight, last ? radiusBottomLeft : innerRadiusBottomLeft];
    var standardDir = ['radiusTopLeft', 'radiusTopRight', 'radiusBottomRight', 'radiusBottomLeft'];
    // Transpose: rotate it clockwise by 90.
    if (isTranspose(coordinate)) {
      standardDir.push(standardDir.shift());
    }
    var extendedStyle = Object.assign(Object.assign({
      radius: radius
    }, Object.fromEntries(standardDir.map(function (d, i) {
      return [d, standardDirRadius[i]];
    }))), {
      inset: inset,
      insetLeft: insetLeft,
      insetRight: insetRight,
      insetBottom: insetBottom,
      insetTop: insetTop,
      minWidth: minWidth,
      maxWidth: maxWidth
    });
    return select(rect(document, points, value, coordinate, extendedStyle)).call(applyStyle, restDefaults).style('fill', 'transparent').style(colorAttribute, color).style(toOpacityKey(options), opacity).style('lineWidth', lineWidth).style('stroke', stroke === undefined ? color : stroke)
    // shape.style has higher priority.
    .call(applyStyle, rest).node();
  };
};
// @todo Should Shape have default animations using for ordinal scale?
Color.props = {
  defaultEnterAnimation: 'scaleInY',
  defaultUpdateAnimation: 'morphing',
  defaultExitAnimation: 'fadeOut'
};